import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Gavel, Play, CheckCircle2, XCircle, ArrowRight, ThumbsUp, ThumbsDown,
  ShieldCheck, Stethoscope, Sparkles,
} from "lucide-react";
import { api } from "../../lib/api";

const FILTERS = [
  { key: "Pending Review", label: "CEO Approval Queue" },
  { key: "Approved", label: "Approved" },
  { key: "Suggested", label: "Suggested" },
  { key: "Rejected", label: "Rejected" },
];

const scoreColor = (s) => (s >= 90 ? "bg-success text-white" : s >= 80 ? "bg-gold text-white" : s >= 60 ? "bg-amber-500 text-white" : "bg-charcoal/40 text-white");

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl border border-border bg-white px-4 py-3" data-testid={testid}>
    <p className="text-2xl font-serif text-navy">{value}</p>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
  </div>
);

const Bullets = ({ title, items, icon: Icon, tone }) => (
  items && items.length > 0 ? (
    <div>
      <p className={`mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${tone}`}><Icon className="h-3.5 w-3.5" /> {title}</p>
      <ul className="space-y-1">
        {items.map((t, i) => <li key={i} className="text-sm leading-relaxed text-charcoal/80">• {t}</li>)}
      </ul>
    </div>
  ) : null
);

const MatchCard = ({ m, onAction, highlight }) => {
  const [busy, setBusy] = useState(false);
  const act = async (kind) => {
    setBusy(true);
    try {
      await api.post(`/admin/matching/${m.id}/${kind}`);
      toast.success(kind === "approve" ? "Approved — cleared for submission" : "Match rejected");
      onAction();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };
  return (
    <div className={`rounded-2xl border bg-white p-6 transition-all ${highlight ? "border-gold ring-2 ring-gold/40 shadow-lg" : "border-border"}`} data-testid={`match-card-${m.id}`} id={`match-card-${m.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-navy">
            <span className="font-serif text-lg">{m.candidate_name || "Candidate"}</span>
            <ArrowRight className="h-4 w-4 text-gold" />
            <span className="font-serif text-lg">{m.job_title || "Role"}</span>
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">{m.company_name} · {m.profession}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`inline-flex h-14 w-14 items-center justify-center rounded-full text-lg font-bold ${scoreColor(m.score)}`} data-testid={`match-score-${m.id}`}>{m.score}%</span>
          <span className="text-[11px] font-semibold text-charcoal/60">{m.verdict}</span>
        </div>
      </div>

      <div className="mt-4 rounded-xl bg-navy/[0.03] p-4">
        <p className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-navy"><Gavel className="h-3.5 w-3.5" /> The Judge — Rationale</p>
        <p className="text-sm leading-relaxed text-charcoal/85">{m.alignment_summary || m.match_rationale}</p>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Bullets title="Pros" items={m.pros} icon={ThumbsUp} tone="text-success" />
        <Bullets title="Cons" items={m.cons} icon={ThumbsDown} tone="text-destructive" />
        <Bullets title="Competencies Matched" items={m.competencies_matched} icon={CheckCircle2} tone="text-navy" />
        <Bullets title="Gaps" items={m.gaps} icon={XCircle} tone="text-amber-600" />
      </div>

      {(m.clinical_match || m.work_rights_fit) && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {m.clinical_match && (
            <div className="rounded-lg border border-border p-3">
              <p className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-navy"><Stethoscope className="h-3.5 w-3.5" /> Clinical / Professional Match</p>
              <p className="text-sm text-charcoal/80">{m.clinical_match}</p>
            </div>
          )}
          {m.work_rights_fit && (
            <div className="rounded-lg border border-border p-3">
              <p className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-navy"><ShieldCheck className="h-3.5 w-3.5" /> Work Rights Fit</p>
              <p className="text-sm text-charcoal/80">{m.work_rights_fit}</p>
              {m.candidate_work_rights?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {m.candidate_work_rights.map((w) => <span key={w} className="rounded-full bg-navy/5 px-2 py-0.5 text-[11px] font-medium text-navy">{w}</span>)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {m.status === "Pending Review" ? (
        <div className="mt-5 flex items-center gap-3">
          <button onClick={() => act("approve")} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-success px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60" data-testid={`match-approve-${m.id}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Approve for Submission
          </button>
          <button onClick={() => act("reject")} disabled={busy} className="inline-flex items-center gap-2 rounded-lg border border-destructive px-5 py-2.5 text-sm font-semibold text-destructive hover:bg-destructive/5 disabled:opacity-60" data-testid={`match-reject-${m.id}`}>
            <XCircle className="h-4 w-4" /> Reject
          </button>
        </div>
      ) : (
        <p className="mt-4 text-xs font-semibold text-muted-foreground">Status: {m.status}{m.reviewed_by ? ` · by ${m.reviewed_by}` : ""}</p>
      )}
    </div>
  );
};

export default function MatchQueue({ focusMatchId, onFocusHandled }) {
  const [filter, setFilter] = useState("Pending Review");
  const [matches, setMatches] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [autoMode, setAutoMode] = useState(null);
  const [savingAuto, setSavingAuto] = useState(false);
  const [highlightId, setHighlightId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [q, s, cfg] = await Promise.all([
        api.get(`/admin/matching/queue?status=${encodeURIComponent(filter)}`),
        api.get(`/admin/matching/stats`),
        api.get(`/admin/matching/settings`),
      ]);
      setMatches(q.data);
      setStats(s.data);
      setAutoMode(cfg.data.auto_mode);
    } catch { toast.error("Failed to load match queue"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  // Deep-link from notification bell: ensure Pending Review filter, then scroll + highlight
  useEffect(() => {
    if (focusMatchId) setFilter("Pending Review");
  }, [focusMatchId]);

  useEffect(() => {
    if (!focusMatchId || loading) return;
    const exists = matches.some((m) => m.id === focusMatchId);
    if (!exists) return;
    setHighlightId(focusMatchId);
    const el = document.getElementById(`match-card-${focusMatchId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    onFocusHandled?.();
    const t = setTimeout(() => setHighlightId(null), 3000);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [focusMatchId, loading, matches]);

  const toggleAuto = async () => {
    setSavingAuto(true);
    try {
      const { data } = await api.put(`/admin/matching/settings`, { auto_mode: !autoMode });
      setAutoMode(data.auto_mode);
      toast.success(`Auto Mode ${data.auto_mode ? "ON — agents process new CVs & job orders automatically" : "OFF — background processing paused"}`);
    } catch { toast.error("Failed to update Auto Mode"); }
    finally { setSavingAuto(false); }
  };

  const runAll = async () => {
    setRunning(true);
    toast.info("Running the 3-agent engine… analysing profiles and judging matches.");
    try {
      const { data } = await api.post(`/admin/matching/run-all`);
      if (data.started === false) { toast.message("A run is already in progress."); }
      if (data.ai_available === false) { toast.error("AI engine unavailable (LLM key inactive)."); setRunning(false); return; }
      // poll run-status until finished
      const poll = async () => {
        try {
          const { data: st } = await api.get(`/admin/matching/run-status`);
          if (st.running) { setTimeout(poll, 3000); return; }
          if (st.phase === "error") toast.error("Engine run failed.");
          else toast.success(`Analysed ${st.candidates_analyzed || 0} candidate(s), ${st.jobs_analyzed || 0} job(s) · ${st.matches_generated || 0} match(es) judged.`);
          setRunning(false);
          await load();
        } catch { setRunning(false); }
      };
      setTimeout(poll, 3000);
    } catch { toast.error("Engine run failed"); setRunning(false); }
  };

  return (
    <div data-testid="match-queue-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl text-navy">CEO Approval Queue</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            The Judge (Agent #2) cross-analyses candidate CV intelligence (Agent #1) with job order intelligence (Agent #3). Top matches land here. <span className="font-semibold text-navy">No profile is submitted to a client without your approval.</span>
          </p>
        </div>
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <button
            onClick={toggleAuto}
            disabled={savingAuto || autoMode === null}
            className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 ${autoMode ? "border-success bg-success/10 text-success" : "border-border bg-white text-charcoal"}`}
            data-testid="auto-mode-toggle"
            title="Master switch for automatic event-driven processing"
          >
            <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${autoMode ? "bg-success" : "bg-charcoal/30"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoMode ? "translate-x-4" : "translate-x-1"}`} />
            </span>
            Auto Mode: <span data-testid="auto-mode-state">{autoMode === null ? "…" : autoMode ? "ON" : "OFF"}</span>
          </button>
          <button onClick={runAll} disabled={running} className="inline-flex items-center justify-center gap-2 rounded-lg bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-light disabled:opacity-70" data-testid="run-matching-btn">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Run Intelligence &amp; Matching
          </button>
        </div>
      </div>

      {stats && (
        <div className="mt-5 grid grid-cols-3 gap-3 sm:grid-cols-6">
          <Stat label="Pending" value={stats.pending} testid="stat-pending" />
          <Stat label="Approved" value={stats.approved} testid="stat-approved" />
          <Stat label="Suggested" value={stats.suggested} testid="stat-suggested" />
          <Stat label="Rejected" value={stats.rejected} testid="stat-rejected" />
          <Stat label="Cand. Ready" value={stats.candidates_ready} testid="stat-cand-ready" />
          <Stat label="Jobs Ready" value={stats.jobs_ready} testid="stat-jobs-ready" />
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${filter === f.key ? "bg-navy text-white" : "border border-border text-charcoal hover:border-navy"}`} data-testid={`filter-${f.key.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-5 space-y-4">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
        ) : matches.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-white py-16 text-center" data-testid="match-empty">
            <Gavel className="mx-auto h-8 w-8 text-charcoal/30" />
            <p className="mt-3 text-sm text-muted-foreground">No matches in "{filter}". Run the engine to generate matches.</p>
          </div>
        ) : (
          matches.map((m) => <MatchCard key={m.id} m={m} onAction={load} highlight={highlightId === m.id} />)
        )}
      </div>
    </div>
  );
}
